import * as configUtils from "util/config";
import * as i18n from "util/i18n";

/**
 * @returns {boolean} valor de configuración {@code masspayments.apicmf.enabled}
 */
export const isMasspaymentsCmfApiEnabled = () => configUtils.getBoolean("masspayments.apicmf.enabled", false);

/**
 * Clave i18n para la etiqueta del destino (CBU, CVU o alias) en listados/modales de transferencias masivas en ON.
 *
 * @returns {string} clave para {@code i18n.get}
 */
export const getMultipleTransferDestinationLabelKey = () => {
    if (isMasspaymentsCmfApiEnabled()) {
        return "multiple.transfers.modal.destination.cmf.alias.label";
    }
    return "multiple.transfers.modal.cbu.label";
};

export const getMultipleTransferDestinationLabelText = () => {
    if (isMasspaymentsCmfApiEnabled()) {
        return i18n.get("multiple.transfers.modal.destination.cmf.alias.label", "CBU/CVU/ALIAS:");
    }
    return i18n.get("multiple.transfers.modal.cbu.label", "CBU:");
};

/** Clave i18n: destino CMF en comprobante individual / PDF (sin ":"; el layout del ticket separa etiqueta y valor). */
export const MULTIPLE_TRANSFER_TICKET_CMF_DESTINATION_LABEL_I18N_KEY =
    "multiple.transfers.individual.data.cbuDestinationCmf.alias.label";

/** Etiqueta del cupón Coelsa (ARBCMFIMP-18712); solo se usa con {@link #isMasspaymentsCmfApiEnabled}. */
export const MULTIPLE_TRANSFER_VOUCHER_COELSA_ID_LABEL_I18N_KEY = "multiple.transfers.voucherNumber.cmf.label";

/**
 * Texto resuelto para comprobante/PDF masivos CMF.
 * Usa {@link #MULTIPLE_TRANSFER_TICKET_CMF_DESTINATION_LABEL_I18N_KEY}.
 */
export const getMultipleTransferTicketDestinationCmfLabelText = () =>
    i18n.get(MULTIPLE_TRANSFER_TICKET_CMF_DESTINATION_LABEL_I18N_KEY, "CBU/CVU/ALIAS");

/**
 * CVU numérico: 22 dígitos y prefijo {@code 000}. Si no aplica (p. ej. alias), devuelve false.
 *
 * @param {*} value destino tal como en comprobante masivo
 * @returns {boolean}
 */
export const isValidCvuNumeric = (value) => {
    const s = value == null ? "" : String(value).trim();
    return /^\d{22}$/.test(s) && s.startsWith("000");
};

/**
 * Máximo de líneas por archivo (cliente), alineado al backend
 * {@code ConfigurationUtils#getMultipleTransferUploadMaxLines}.
 *
 * @returns {number}
 */
export const getMultipleTransferUploadMaxLinesClient = () => {
    if (isMasspaymentsCmfApiEnabled()) {
        return configUtils.getInteger("masspayments.file.max.number", 1000);
    }
    return configUtils.getInteger("frontend.MultipleTransfer.max.allowed.lines", 100);
};

/**
 * Texto informativo cuando los masivos se procesan por API CMF.
 * Solo desde configuración {@code masspayments.receipt.text} (sin i18n).
 *
 * @returns {string|null} texto a mostrar, o null si no aplica o está vacío
 */
export const getMasspaymentsApiReceiptHint = () => {
    if (!isMasspaymentsCmfApiEnabled()) {
        return null;
    }
    const configured = configUtils.get("masspayments.receipt.text");
    if (configured == null || String(configured).trim() === "") {
        return null;
    }
    return String(configured).trim();
};

/**
 * Evita mostrar "null" / vacío en comprobantes cuando CMF no devuelve titular o banco.
 *
 * @param {*} value valor crudo
 * @returns {string} cadena segura para ticket/PDF
 */
export const sanitizeMasspaymentCmfDisplayString = (value) => {
    if (value == null) {
        return "";
    }
    const s = String(value).trim();
    if (s === "" || s.toLowerCase() === "null" || s.toLowerCase() === "undefined") {
        return "";
    }
    return s;
};

/**
 * Normaliza el JSON de error de una línea masiva para modal / comprobante.
 * Solo expone las claves {@code code} y {@code description} de cada objeto (mismo contrato que persiste el backend).
 *
 * @param {*} raw string JSON, array u objeto
 * @returns {Array<{code: string, description: string}>|null} lista para {@code BatchDetailErrorsModal};
 *     o null si no aplica
 */
export const normalizeMultipleTransferLineErrorsForModal = (raw) => {
    if (raw == null) {
        return null;
    }
    try {
        let parsed;
        if (typeof raw === "string") {
            const t = raw.trim();
            if (!t) {
                return null;
            }
            parsed = JSON.parse(t);
        } else if (Array.isArray(raw)) {
            parsed = raw;
        } else if (typeof raw === "object") {
            parsed = [raw];
        } else {
            return null;
        }
        const list = Array.isArray(parsed) ? parsed : [parsed];
        const out = list
            .filter((item) => item && typeof item === "object")
            .map((item) => {
                const code = item.code != null ? String(item.code).trim() : "";
                const description = item.description != null ? String(item.description).trim() : "";
                return { code, description };
            })
            .filter((x) => x.code.length > 0 || x.description.length > 0);
        return out.length > 0 ? out : null;
    } catch (e) {
        return null;
    }
};

/**
 * Limpia valores string de un objeto ticket masivo (solo campos con format "string").
 *
 * @param {object} transferData estructura tipo {@code transferData} del menú contextual
 * @returns {object}
 */
export const sanitizeMasspaymentCmfTicketTransferData = (transferData) => {
    if (!transferData || typeof transferData !== "object") {
        return transferData;
    }
    const out = { ...transferData };
    Object.keys(out).forEach((key) => {
        const item = out[key];
        if (
            item &&
            typeof item === "object" &&
            Object.prototype.hasOwnProperty.call(item, "value") &&
            item.format === "string"
        ) {
            out[key] = { ...item, value: sanitizeMasspaymentCmfDisplayString(item.value) };
        }
    });
    return out;
};

/**
 * Con API CMF masivos activa: la HU permite anular en **PENDIENTE DE FIRMA** y **PENDIENTE DE ENVÍO** igual que
 * sin CMF. Se oculta anular si el lote pasó a **RECEIVED** / **FINISHED**, donde la anulación ya no aplica igual
 * que hoy.
 * (Si en el futuro se expone de nuevo `cmfBatchId` al cliente, se puede reforzar la regla con ese dato.)
 *
 * @param {object} batch objeto lote (batchStatus)
 * @returns {boolean} true si se permite mostrar / usar anular lote
 */
export const isMultipleTransferUserCancelAllowed = (batch) => {
    if (!isMasspaymentsCmfApiEnabled() || !batch) {
        return true;
    }
    const status = batch.batchStatus;
    if (!status) {
        return true;
    }
    const u = String(status).toUpperCase();
    if (u === "RECEIVED") {
        return false;
    }
    if (u.startsWith("FINISHED")) {
        return false;
    }
    return true;
};
